import React, { useState, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  GridLegacy as Grid,
  Chip,
  Alert,
  ImageList,
  ImageListItem,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { IIssueAtLocationOutput, IssueStatus, api } from "../../../../logic/api";
import { SessionContext } from "../../../../utility/useSession";
import { useDate } from "../../../../utility/useDate";
import { getFileUrl } from "../../../../utility/utils";
import { getStatusLabel, getStatusColorMui, getIssueTypeLabel } from "./issueUtils";
import { EditableGroup, EditableGroupType } from "../../../../components/editable/EditableGroup/EditableGroup";
import { EditableField, EditableFieldTypes } from "../../../../components/editable/EditableField/EditableField";
import { IDropDownListItem } from "../../../../components/editable/EditableField/fields/DropDownField";
import styles from "./styles";

interface IssueDetailDialogProps {
  issue: IIssueAtLocationOutput | null;
  open: boolean;
  onClose: () => void;
  onStatusUpdate: () => void;
}

interface IssueFormData {
  status: IssueStatus;
}


const IssueDetailDialog: React.FunctionComponent<IssueDetailDialogProps> = ({
  issue,
  open,
  onClose,
  onStatusUpdate,
}) => {
  const { t } = useTranslation();
  const session = useContext(SessionContext);
  const { toFormattedDateTimeString } = useDate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const { issueDetailDialog } = styles;

  const [formData, setFormData] = useState<IssueFormData>({
    status: issue?.status || IssueStatus.Open
  });
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  const updateStatusHook = api.hooks.vehicleManager.updateIssue();

  React.useEffect(() => {
    if (issue) {
      setFormData({ status: issue.status });
      setUpdateError(null);
      setUpdateSuccess(false);
    }
  }, [issue]);


  const handleSaveStatus = async () => {
    if (!issue || formData.status === issue.status) {
      return;
    }

    setIsUpdatingStatus(true);
    setUpdateError(null);

    try {
      await new Promise<void>((resolve, reject) => {
        updateStatusHook.send(session.current.context, {
          issueId: issue.id,
          status: formData.status,
        }, (result) => {
          if (result && result.success) {
            setUpdateSuccess(true);
            setTimeout(() => {
              onStatusUpdate();
            }, 1000);
            resolve();
          } else {
            reject(new Error(t("locations.vehicleIssues.messages.updateError")));
          }
        });
      });
    } catch (error) {
      setUpdateError(error instanceof Error ? error.message : t("locations.vehicleIssues.messages.updateError"));
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleCancel = () => {
    if (issue) {
      setFormData({ status: issue.status });
    }
    setUpdateError(null);
    setUpdateSuccess(false);
    onClose();
  };

  if (!issue) {
    return null;
  }

  const hasStatusChanged = formData.status !== issue.status;

  return (
    <>
      <Dialog
        data-testid="issue-detail-dialog"
        open={open}
        onClose={handleCancel}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexDirection: "column" }}>
            <Typography variant="h6">
              {t("locations.vehicleIssues.details.title")}
            </Typography>
            <Chip
              label={getStatusLabel(issue.status, t)}
              color={getStatusColorMui(issue.status)}
              size="small"
            />
          </Box>
        </DialogTitle>

        <DialogContent>
          {updateError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {updateError}
            </Alert>
          )}

          {updateSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t("locations.vehicleIssues.messages.updateSuccess")}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {getIssueTypeLabel(issue.issue, t)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {issue.issueType}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: "bold" }}>
                {t("locations.vehicleIssues.fields.location")}
              </Typography>
              <Typography variant="body2">
                {issue.locationName} - {issue.locationParentName}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: "bold" }}>
                {t("locations.vehicleIssues.fields.vehicle")}
              </Typography>
              <Typography variant="body2">
                {issue.vehicleReg} {issue.vehicleMake} {issue.vehicleModel}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {issue.vehicleColor}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: "bold" }}>
                {t("locations.vehicleIssues.fields.recordedBy")}
              </Typography>
              <Typography variant="body2">
                {issue.recordedBy || "-"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {toFormattedDateTimeString(new Date(issue.recordedAt))}
              </Typography>
            </Grid>

            {issue.movedBy && (
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: "bold" }}>
                  {t("locations.vehicleIssues.fields.movedBy")}
                </Typography>
                <Typography variant="body2">
                  {issue.movedBy}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {toFormattedDateTimeString(new Date(issue.movedAt))}
                </Typography>
              </Grid>
            )}

            {issue.expectedReturnTime && (
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: "bold" }}>
                  {t("locations.vehicleIssues.fields.expectedReturn")}
                </Typography>
                <Typography variant="body2">
                  {toFormattedDateTimeString(new Date(issue.expectedReturnTime))}
                </Typography>
              </Grid>
            )}

            {issue.bookingRef && (
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: "bold" }}>
                  Booking Reference
                </Typography>
                <Typography variant="body2">
                  {issue.bookingRef}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              {/* editable field... adds a 10px padding which we don't want, so this is a workaround */}
              <Box sx={{ mx: -1.25 }}>
                <EditableGroup<IssueFormData>
                  id="issue-detail-form"
                  testId="issue-detail-form"
                  data={formData}
                  editType={EditableGroupType.alwaysEditing}
                  onDataChanged={async (data) => {
                    setFormData(data);
                    return true;
                  }}
                >
                  <EditableField<IssueFormData, IDropDownListItem>
                    label={t("locations.vehicleIssues.details.updateStatus")}
                    inputType={EditableFieldTypes.enumDropDownField}
                    onProvide={(data) => ({
                      value: data.status.toString(),
                      name: getStatusLabel(data.status, t)
                    })}
                    onRequestData={async () => [
                      { value: IssueStatus.Open.toString(), name: getStatusLabel(IssueStatus.Open, t) },
                      { value: IssueStatus.Assigned.toString(), name: getStatusLabel(IssueStatus.Assigned, t) },
                      { value: IssueStatus.Resolved.toString(), name: getStatusLabel(IssueStatus.Resolved, t) },
                      { value: IssueStatus.NoAction.toString(), name: getStatusLabel(IssueStatus.NoAction, t) }
                    ]}
                    onChange={(data, value) => {
                      data.status = parseInt(value.value || "0") as IssueStatus;
                    }}
                  />
                </EditableGroup>
              </Box>
            </Grid>

            {issue.images && issue.images.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: "bold" }}>
                  {t("locations.vehicleIssues.fields.images")} ({issue.images.length})
                </Typography>
                <ImageList
                  sx={{ width: "100%", height: "auto" }}
                  cols={isMobile ? 2 : isTablet ? 3 : 4}
                  gap={8}
                >
                  {issue.images.map((image, index) => (
                    <ImageListItem
                      key={index}
                      data-testid={`issue-image-${index}`}
                      sx={issueDetailDialog.imageListItem}
                      onClick={() => window.open(getFileUrl(session, image), "_blank")}
                    >
                      <img
                        src={getFileUrl(session, image)}
                        alt={`Issue image ${index + 1}`}
                        loading="lazy"
                        style={{
                          width: "100%",
                          height: 150,
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            data-testid="issue-detail-cancel"
            onClick={handleCancel}
            variant="text"
            disabled={isUpdatingStatus}
            sx={issueDetailDialog.issueDetailCancelButton}
            disableRipple
            disableElevation
          >
            {t("locations.vehicleIssues.details.cancel")}
          </Button>
          <Button
            data-testid="issue-detail-save"
            onClick={handleSaveStatus}
            variant="contained"
            disabled={!hasStatusChanged || isUpdatingStatus}
            disableRipple
            disableElevation
            sx={issueDetailDialog.issueDetailSaveButton}
          >
            {isUpdatingStatus ? "Updating..." : t("locations.vehicleIssues.details.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IssueDetailDialog;